/*
 * "Your usual size" - pre-selects a logged-in customer's usual size on the product page (snippets/jhr-usual-size.liquid).
 * Aaron, 26 Sept 2026: "make sure that this is displayed correctly and not too intrusive". So it:
 *   - does nothing on a link to a particular size (?variant=), or once the shopper has tapped the picker;
 *     guests only get it on horse items, from a size they saved themselves (see below, 29 Sep);
 *   - only picks a size that is in stock, exactly as if the shopper had picked it (the theme's own drop-down /
 *     buttons do the work, so price, stock and the address update as normal);
 *   - stores nothing in the browser for logged-in customers (a guest's own saved horse size is read from
 *     localStorage "jhr_horse", written by assets/jhr-horse-save.js when they tap "Yes, save").
 * 28 Sept 2026 (V19.2), Aaron: the line under the picker was "taking up space ... doesn't look particularly good" -
 * instead "a little label across the size ... a toast that comes up saying 'We've added your usual size'". So the only
 * sign now is a small label INSIDE the size box, beside the chosen size. It takes no room on the page. Aaron picked
 * its look (a soft tag like the delivery box) and wording "Your Usual Size" on 28 Sep.
 * 28 Sept 2026, Aaron's phone test: it "came up and went away way too quickly". Now:
 *   - it waits until the size box has sat properly on screen for a moment (SETTLE_MS), not while a thumb flicks past;
 *   - it stays up for SHOW_MS of time the shopper can actually see it - the countdown pauses while the box is off
 *     screen;
 *   - only a real tap on the picker (or a key) puts it away. A touch that starts a scroll does not - on a phone the
 *     thumb often lands on the size box just to keep scrolling.
 * 29 Sept 2026 (Aaron, "should it not be there the whole time?"): the tag in the closed box now STAYS for as long as
 * that size is chosen, and follows the choice ("✓ Otto's Size" -> "✓ Bella's Size" -> none); the open list tags every
 * size that is theirs. The timed 3-second label is kept only for "your usual size is out of stock".
 * 29 Sept 2026 (Aaron): riding boots with a "Calf Width" choice (Athena: Standard / Wide) get their usual calf width
 * picked too, after the size, with its own "Your Usual Width" label - only when that size comes in stock in that width.
 * The size itself comes from the Englander Search app (/apps/jhr/my-size): Shopify signs that request and tells the
 * app which customer is logged in, so a page can only ever learn its own shopper's usual size.
 */
(function () {
  var anchor = document.querySelector('[data-jhr-usual-size]');
  if (!anchor || anchor.dataset.jhrStarted) return;
  anchor.dataset.jhrStarted = '1';
  // They came for a particular size (a ?variant= link, or a reload after choosing): never pick over it - but the
  // tags showing which sizes are theirs still go on.
  var cameForSize = new URLSearchParams(window.location.search).has('variant');
  // A guest who saved their horse's size on this device (29 Sep, assets/jhr-horse-save.js): the same picking and
  // tagging, fed from their browser instead of the app ("Your Horse" instead of a horse's name).
  var guest = anchor.dataset.guest === '1';

  var SETTLE_MS = 600;    // the size box must stay on screen this long before the label comes up
  var SHOW_MS = 3000;     // how long the label stays up, counting only time the box is on screen (Aaron: 3 s)
  var ON_SCREEN = 0.9;    // share of the size box that must be inside the viewing area
  var section = anchor.closest('.shopify-section') || document;
  var acted = false;      // the shopper has tapped the size picker
  var toasts = [];        // the label(s) on screen: the size box, and the calf-width box on riding boots
  var stop = function () {};

  function hide() {
    var els = toasts;
    toasts = [];
    els.forEach(function (el) {
      el.classList.remove('is-on');
      window.setTimeout(function () { el.remove(); markBoxes(); }, 300);
    });
  }
  // A tap (click) or a key on the picker is a choice. pointerdown is NOT: it also starts every scroll.
  function watch(evt) {
    if (evt.isTrusted && evt.target && evt.target.closest && evt.target.closest('variant-picker')) {
      acted = true;
      stop();
      hide();
    }
  }
  section.addEventListener('click', watch, true);
  section.addEventListener('keydown', watch, true);

  function sizeBox(position) {
    var picker = section.querySelector('variant-picker');
    return picker && picker.querySelector('.option-selector[data-index="' + position + '"]');
  }

  /*
   * 'picked', 'already' (it was selected anyway) or false (this product has no such size).
   * After a size change the theme moves keyboard focus to the size box (assets/variant-picker.js, "Restore focus"), and
   * the browser scrolls to whatever has focus - on a phone the page jumped straight down to the size box on arrival
   * (Aaron, 28 Sep 2026). The theme's own opt-out is detail.noFocus on the change event, so our pick carries it.
   */
  function pick(box, value) {
    // Drop-down style (every JHR product template): the theme's custom-select has selectOption().
    var select = box.querySelector('custom-select');
    if (select) {
      var options = select.querySelectorAll('[data-value]');
      for (var i = 0; i < options.length; i++) {
        if (options[i].dataset.value === value) {
          if (options[i].getAttribute('aria-selected') === 'true') return 'already';
          if (typeof select.selectOption !== 'function') return false;
          // selectOption() sends its change event straight away; mark it before the theme's picker hears it.
          var noFocus = function (evt) { if (evt.target === select && evt.detail) evt.detail.noFocus = true; };
          section.addEventListener('change', noFocus, true);
          try { select.selectOption(options[i]); } finally { section.removeEventListener('change', noFocus, true); }
          return 'picked';
        }
      }
      return false;
    }
    // Button style: a radio per size.
    var inputs = box.querySelectorAll('input[type="radio"]');
    for (var j = 0; j < inputs.length; j++) {
      if (inputs[j].value === value) {
        if (inputs[j].checked) return 'already';
        inputs[j].checked = true;
        inputs[j].dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { noFocus: true } }));
        return 'picked';
      }
    }
    return false;
  }

  // Puts the (empty) label into the size box; fills and fades it in a frame later. False if this box can't take one.
  // `spoken` is read out before the text by screen readers only (the tag itself just says "Your Usual Size").
  // `keep`: add beside the labels already up (the calf-width box) instead of replacing them.
  function showLabel(box, text, tick, spoken, keep) {
    var wrap = box && box.querySelector('.custom-select');   // the drop-down's own positioned wrapper round its button
    if (!wrap) return false;                                // button-style pickers show the choice plainly - no label
    if (!keep) hide();
    var el = document.createElement('span');
    el.className = 'jhr-usual-toast';
    el.setAttribute('role', 'status');
    wrap.appendChild(el);
    toasts.push(el);
    // Filled a frame later so screen readers announce it; then check it fits beside the chosen size.
    window.requestAnimationFrame(function () {
      if (toasts.indexOf(el) < 0) return;
      if (tick) {
        var t = document.createElement('span');
        t.className = 'jhr-usual-toast__tick';
        t.setAttribute('aria-hidden', 'true');
        t.textContent = '✓';
        el.appendChild(t);
      }
      if (spoken) {
        var sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = spoken;
        el.appendChild(sr);
      }
      el.appendChild(document.createTextNode(text));
      var chosen = wrap.querySelector('.custom-select__btn > span');
      if (chosen && el.getBoundingClientRect().left < chosen.getBoundingClientRect().right + 12) {
        el.classList.add('jhr-usual-toast--edge');     // a long size name: sit on the box's top edge instead
      }
      void el.offsetWidth;                             // start the fade from the hidden state
      el.classList.add('is-on');
    });
    return true;
  }

  /*
   * Brings the label up once the size box is properly on screen, and keeps it up for SHOW_MS of on-screen time.
   * The theme swaps in a fresh copy of the picker whenever the size changes (including our own pick), so the picker's
   * container is watched and the CURRENT size box is always the one being checked.
   * Note: the browser's on-screen check reports "intersecting" for ANY overlap, however small - it is the share of the
   * box on screen (intersectionRatio) that decides.
   */
  function showWhenSeen(position, text, tick, spoken, extras) {
    // the size box's label, plus any extra boxes' labels (riding boots: the calf width) - up and down together
    function showAll(box) {
      if (!showLabel(box, text, tick, spoken)) return false;
      (extras || []).forEach(function (x) { showLabel(sizeBox(x.position), x.text, true, x.spoken, true); });
      return true;
    }
    if (!('IntersectionObserver' in window)) { showAll(sizeBox(position)); window.setTimeout(hide, SHOW_MS); return; }
    var parent = anchor.parentNode;
    var watched = null;
    var seen = false;       // the box is on screen now
    var shown = false;      // the label has come up
    var settle = null;
    var clock = null;
    var left = SHOW_MS;
    var since = 0;

    function startClock() { since = Date.now(); clock = window.setTimeout(function () { stop(); hide(); }, left); }
    function pauseClock() { window.clearTimeout(clock); clock = null; left -= Date.now() - since; }
    function setSeen(now) {
      if (now === seen) return;
      seen = now;
      if (!shown) {
        window.clearTimeout(settle);
        if (now) {
          settle = window.setTimeout(function () {
            if (acted || !seen) return;
            if (!showAll(watched)) { stop(); return; }
            shown = true;
            startClock();
          }, SETTLE_MS);
        }
        return;
      }
      if (now) startClock(); else pauseClock();
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.target === watched) setSeen(e.isIntersecting && e.intersectionRatio >= ON_SCREEN);
      });
    }, { rootMargin: '-15% 0px -10% 0px', threshold: [0, ON_SCREEN] });

    function track() {
      var box = sizeBox(position);
      if (!box || box === watched) return;
      if (watched) io.unobserve(watched);
      watched = box;
      if (shown && toasts.length && !toasts[0].isConnected) showAll(box);   // swapped by the theme: carry them over
      if (seen) setSeen(false);          // the new box reports its own position straight away
      io.observe(box);
    }
    var mo = new MutationObserver(track);
    mo.observe(parent, { childList: true });
    stop = function () {
      mo.disconnect();
      io.disconnect();
      window.clearTimeout(settle);
      window.clearTimeout(clock);
      stop = function () {};
    };
    track();
  }

  // Picks a value in one box, then waits for the theme to finish swapping in the fresh picker (normally well under a
  // second) before calling done('picked' | 'already' | false).
  function pickAndSettle(position, value, done) {
    var box = sizeBox(position);
    var result = box ? pick(box, value) : false;
    acted = false;                                   // our own pick is not the shopper choosing
    if (result !== 'picked') { done(result); return; }
    var parent = anchor.parentNode;
    var quiet = null;
    var over = false;
    var swapped = new MutationObserver(function () {
      window.clearTimeout(quiet);
      quiet = window.setTimeout(end, 150);
    });
    var cap = window.setTimeout(end, 3000);
    function end() {
      if (over) return;
      over = true;
      swapped.disconnect();
      window.clearTimeout(cap);
      window.clearTimeout(quiet);
      done(result);
    }
    swapped.observe(parent, { childList: true });
  }

  /*
   * Small tags INSIDE the open size list, on the right of the sizes that are theirs (Aaron, 29 Sep): "Otto's Size" /
   * "Bella's Size" for their horses (only sizes the app is sure of), "Your Usual Size" for their usual size when it is
   * no horse's, "Your Usual Width" on a boot's calf width. Nothing is added to the page itself - and unlike the 3-second
   * label they are there every time the list is opened. Put back whenever the theme swaps in a fresh picker. The
   * drop-down copies only an option's FIRST span into its button, so the closed box still just says "Cob".
   */
  var listTags = {};      // box position -> option value -> [tag text]
  var tagWatch = null;
  function addTag(position, value, text) {
    var box = listTags[position] = listTags[position] || {};
    (box[value] = box[value] || []).push(text);
  }
  function applyTags() {
    Object.keys(listTags).forEach(function (position) {
      var box = sizeBox(position);
      if (!box) return;
      var items = box.querySelectorAll('.custom-select__option[data-value]');
      for (var i = 0; i < items.length; i++) {
        var texts = listTags[position][items[i].dataset.value];
        if (!texts || items[i].querySelector('.jhr-size-tags')) continue;
        var wrap = document.createElement('span');
        wrap.className = 'jhr-size-tags pointer-events-none';
        texts.slice(0, 2).forEach(function (text) {
          var t = document.createElement('span');
          t.className = 'jhr-size-tag';
          t.textContent = text;
          wrap.appendChild(t);
        });
        items[i].appendChild(wrap);
      }
    });
  }
  /*
   * The CLOSED box carries the tag of whatever is chosen, for as long as it is chosen (Aaron, 29 Sep: "should it not be
   * there the whole time?"): Cob chosen = "✓ Otto's Size"; choose Full = "✓ Bella's Size"; choose Pony = no tag. Boots:
   * "✓ Your Usual Size" / "✓ Your Usual Width". Redrawn after every choice and every picker swap. It floats inside
   * the box beside the chevron, so the page never moves.
   */
  function markBoxes() {
    Object.keys(listTags).forEach(function (position) {
      var box = sizeBox(position);
      var wrap = box && box.querySelector('.custom-select');
      if (!wrap) return;
      if (toasts.some(function (t) { return wrap.contains(t); })) return;   // a passing notice is up; back after it
      var sel = box.querySelector('.custom-select__option[aria-selected="true"]');
      var texts = sel ? listTags[position][sel.dataset.value] : null;
      var el = wrap.querySelector('.jhr-usual-toast--kept');
      if (!texts) { if (el) el.remove(); return; }
      if (el && el.dataset.text === texts[0]) return;
      if (!el) {
        el = document.createElement('span');
        el.className = 'jhr-usual-toast jhr-usual-toast--kept';
        wrap.appendChild(el);
      }
      el.dataset.text = texts[0];
      el.textContent = '';
      var tick = document.createElement('span');
      tick.className = 'jhr-usual-toast__tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.textContent = '✓';
      el.appendChild(tick);
      el.appendChild(document.createTextNode(texts[0]));
      var chosen = wrap.querySelector('.custom-select__btn > span');
      el.classList.remove('jhr-usual-toast--edge');
      if (chosen && el.getBoundingClientRect().left < chosen.getBoundingClientRect().right + 12) {
        el.classList.add('jhr-usual-toast--edge');     // a long size name: sit on the box's top edge instead
      }
      void el.offsetWidth;                             // fade in from the hidden state
      el.classList.add('is-on');
    });
  }
  function showTags() {
    applyTags();
    markBoxes();
    if (!tagWatch) {
      tagWatch = new MutationObserver(function () { applyTags(); markBoxes(); });
      tagWatch.observe(anchor.parentNode, { childList: true });
      section.addEventListener('change', function () { window.setTimeout(markBoxes, 0); });   // a choice with no swap
    }
  }

  // 2 Oct 2026: a guest's own saved RIDER sizes (assets/jhr-rider-save.js, localStorage "jhr_sizes"): "For Me" is picked
  // by itself and tagged "Your Size"; an adult's / a child's size is only tagged ("Their Size" / "Your Child's Size") -
  // except on a children's item, where their child's size is picked. Same light matching as jhr-rider-save.js.
  function riderNorm(s) {
    var t = String(s || '').toLowerCase().replace(/["”″]/g, '').replace(/\b(uk|size|adults?|ladies|womens?|mens?)\b/g, ' ');
    var child = /\b(child(ren)?'?s?|kids?|junior|youth)\b/.test(t);
    t = t.replace(/\b(child(ren)?'?s?|kids?|junior|youth)\b/g, ' ').replace(/\s+/g, ' ').trim();
    t = t.replace(/^(extra|x)[- ]?small$/, 'xs').replace(/^small$/, 's').replace(/^medium$/, 'm').replace(/^large$/, 'l')
      .replace(/^(extra|x)[- ]?large$/, 'xl').replace(/^(xx|2x)[- ]?large$/, 'xxl').replace(/[\s-]/g, '');
    return (child ? 'c:' : '') + t;
  }
  function riderReply(el) {
    var data = null;
    var saved = null;
    try { data = JSON.parse(el.textContent || 'null'); } catch (e) { data = null; }
    try { saved = JSON.parse(window.localStorage.getItem('jhr_sizes') || 'null'); } catch (e) { saved = null; }
    if (!data || !saved || saved.v !== 1) return null;
    function match(want) {
      if (!want) return null;
      var n = riderNorm(want);
      var value = null;
      var inStock = false;
      Object.keys(data.variants).forEach(function (id) {
        var v = data.variants[id];
        if (riderNorm(v[0]) === n) { value = value || v[0]; if (v[1]) inStock = true; }
      });
      return value ? { value: value, inStock: inStock } : null;
    }
    var slots = [['me', 'Your Size'], ['child', "Your Child's Size"], ['adult', 'Their Size']];
    var tags = [];
    var found = {};
    slots.forEach(function (s) {
      var m = match(saved[s[0]] && saved[s[0]][data.kind]);
      if (!m) return;
      found[s[0]] = m;
      if (!tags.some(function (t) { return t.value === m.value; })) tags.push({ text: s[1], value: m.value, position: data.pos });
    });
    if (!tags.length) return null;
    var mine = data.child ? found.child : found.me;
    return { ok: true, horses: tags, select: mine ? { position: data.pos, value: mine.value, label: mine.value, inStock: mine.inStock,
      oos: (data.child ? "Your Child's Size (" : 'Your Size (') + mine.value + ') is out of stock' } : null };
  }

  // The guest's saved horse size, matched against this product's sizes ("6ft 3" = 6'3", case and spaces ignored)
  function guestReply() {
    var riderEl = document.querySelector('script[data-jhr-rider]');
    if (riderEl) return riderReply(riderEl);
    var data = null;
    var saved = null;
    try { data = JSON.parse((document.querySelector('script[data-jhr-horse]') || {}).textContent || 'null'); } catch (e) { data = null; }
    try { saved = JSON.parse(window.localStorage.getItem('jhr_horse') || 'null'); } catch (e) { saved = null; }
    var want = data && saved && saved.sizes && saved.sizes[data.kind];
    if (!want) return null;
    var norm = function (s) { return String(s || '').toLowerCase().replace(/["”″\s]/g, '').replace(/ft/g, "'"); };
    var value = null;
    var inStock = false;
    Object.keys(data.variants).forEach(function (id) {
      var v = data.variants[id];
      if (norm(v[0]) === norm(want)) { value = value || v[0]; if (v[1]) inStock = true; }
    });
    if (!value) return null;
    return { ok: true, select: { position: data.pos, value: value, label: value, inStock: inStock },
      horses: [{ name: 'Your Horse', text: 'Your Horse', value: value, position: data.pos }] };
  }

  (guest ? Promise.resolve(guestReply()) : fetch('/apps/jhr/my-size?product=' + encodeURIComponent(anchor.dataset.productId), {
    credentials: 'same-origin', headers: { Accept: 'application/json' },
  }).then(function (r) { return r.ok ? r.json() : null; }))
    .then(function (d) {
      var horses = (d && d.horses) || [];
      var s = d && d.select;
      horses.forEach(function (h) { addTag(h.position, h.value, h.text || (h.name + "'s Size")); });   // two words (Aaron)
      if (s && !horses.some(function (h) { return h.value === s.value; })) addTag(s.position, s.value, 'Your Usual Size');
      if (s && s.also) addTag(s.also.position, s.also.value, 'Your Usual Width');
      showTags();                                    // list tags + the closed box's tag, kept up to date from now on
      if (!s || acted || cameForSize) return;        // never pick over a size they chose or came for
      if (!sizeBox(s.position)) return;
      if (!s.inStock) {
        showWhenSeen(s.position, s.oos || ((guest ? "Your Horse's Size (" : 'Your Usual Size (') + s.label + ') is out of stock'), false);
        return;
      }
      pickAndSettle(s.position, s.value, function (result) {
        markBoxes();
        if (!result || acted || !s.also) return;
        // Riding boots (29 Sep): their usual calf width too, once the size has settled - the app only sends it when
        // that size comes in stock in that width.
        pickAndSettle(s.also.position, s.also.value, function () { markBoxes(); });
      });
    })
    .catch(function () { /* any trouble: the page simply works as normal */ });
})();
